/**
 * Derives dollar targets per goal from quiz answers (mockup-aligned heuristics).
 */

const roundMoney = (n) => Math.round(Math.max(0, n));

/** College & education */
export function computeEducationTarget(a) {
    const baseAllIn = a.schoolType === 'private' ? 240000 : 95000;
    const ex = a.expenses || {};
    let factor = 0.72;
    if (ex.tuition && ex.roomBoard) factor = 1;
    else if (ex.tuition) factor = 0.68;
    else if (ex.roomBoard) factor = 0.55;
    if (!ex.tuition && !ex.roomBoard) factor = 0.65;
    const booksAdd = ex.books ? 12000 : 0;
    const cover = (Number(a.coverPct) || 100) / 100;
    return roundMoney((baseAllIn * factor + booksAdd) * cover);
}

/** Kid's retirement — lifestyle corpus × kickstart % */
export function computeRetirementTarget(a) {
    const corpus = { modest: 420000, comfortable: 780000, luxury: 1250000 };
    const base = corpus[a.lifestyle] || corpus.comfortable;
    const incomeBoost = Number(a.desiredIncome) > 0 ? Number(a.desiredIncome) / 75000 : 1;
    const kick = (Number(a.kickstartPct) || 50) / 100;
    return roundMoney(base * Math.min(1.4, incomeBoost) * kick);
}

/** Emergency fund */
export function computeEmergencyTarget(a) {
    const monthly = 4500;
    const level = a.level || '1mo';
    if (level === 'starter') return 1000;
    const months = { '1mo': 1, '2mo': 2, '3mo': 3, '4mo': 4, '6mo': 6 };
    const m = months[level] || 1;
    return roundMoney(monthly * m);
}

/** House down payment */
export function computeHouseTarget(a) {
    const price = Number(a.houseValue) || 500000;
    const pct = (Number(a.downPaymentPct) || 20) / 100;
    return roundMoney(price * pct);
}

/** Business startup */
export function computeBusinessTarget(a) {
    const presets = {
        art: 5000,
        fitness: 7000,
        vending: 10000,
        website: 10000,
        ecommerce: 25000,
        photo: 15000,
        construction: 50000,
        retail: 100000,
        foodtruck: 115000
    };
    const base =
        a.amountMode === 'custom'
            ? Number(a.customAmount) || 25000
            : presets[a.businessPreset] || 25000;
    const cover = (Number(a.coverPct) || 50) / 100;
    return roundMoney(base * cover);
}

/** FIRE kickstarter */
export function computeFireTarget(a) {
    const annual = { modest: 50000, comfortable: 75000, luxury: 125000 };
    const income = annual[a.lifestyle] || 75000;
    const corpus = income * 25;
    const kick = (Number(a.kickstartPct) || 50) / 100;
    return roundMoney(corpus * kick);
}

/** General wealth / investments (preset purposes from mockup) */
export function computeWealthTarget(a) {
    const purposeAmounts = {
        literacy: 5000,
        motivate: 25000,
        extracurricular: 50000,
        meals: 125000,
        basic: 600000
    };
    return roundMoney(purposeAmounts[a.purpose] || 25000);
}

export function computeTargetForGoal(goalId, answers) {
    if (!answers) return 0;
    switch (goalId) {
        case 'education':
            return computeEducationTarget(answers);
        case 'retirement':
            return computeRetirementTarget(answers);
        case 'emergency':
            return computeEmergencyTarget(answers);
        case 'house':
            return computeHouseTarget(answers);
        case 'business':
            return computeBusinessTarget(answers);
        case 'fire':
            return computeFireTarget(answers);
        case 'wealth':
            return computeWealthTarget(answers);
        default:
            return 0;
    }
}

export const DEFAULT_ANSWERS = {
    education: {
        schoolType: 'public',
        expenses: { tuition: true, roomBoard: true, books: true },
        coverPct: 50
    },
    retirement: {
        lifestyle: 'comfortable',
        desiredIncome: 75000,
        kickstartPct: 50
    },
    emergency: { level: '1mo' },
    house: { downPaymentPct: 20, houseValue: 500000 },
    business: {
        amountMode: 'preset',
        businessPreset: 'ecommerce',
        customAmount: 25000,
        coverPct: 50
    },
    fire: { fiAge: 40, lifestyle: 'comfortable', kickstartPct: 50 },
    wealth: { purpose: 'motivate' }
};
